import type { messages as ptMessages } from './pt-BR';

/**
 * The English fallback for the tenant self-serve area's own catalogue.
 * `pt-BR.ts` owns the key set; the `Record<keyof typeof ptMessages, string>`
 * annotation below turns a missing (or misspelled) key here into a compile
 * error, and `__tests__/catalogue.test.ts` re-checks the same guarantee at
 * the value level.
 *
 * Every `.source` entry here is simply the same English text as its sibling
 * key — English needs no translation of itself — and exists only so this
 * file's key set matches `pt-BR.ts`'s exactly, same as
 * `estimator.statement.N.source` in `src/i18n/en.ts`.
 */
export const messages: Record<keyof typeof ptMessages, string> = {
  // -- Balance --------------------------------------------------------------
  'balance.total': 'Total',
  'balance.total.source': 'Total',
  'balance.figure': 'Balance',
  'balance.figure.source': 'Balance',
  'balance.available': 'Available',
  'balance.available.source': 'Available',
  'balance.held': 'Set aside',
  'balance.held.source': 'Set aside',

  // -- Members ------------------------------------------------------------
  'team.heading': 'Users',
  'team.heading.source': 'Users',

  'members.heading': 'People',
  'members.heading.source': 'People',
  'members.scope.tenantWide': 'Organisation-wide',
  'members.scope.tenantWide.source': 'Organisation-wide',
  'members.remove.confirm': 'Remove {email} from the organisation? They lose access immediately.',
  'members.remove.confirm.source':
    'Remove {email} from the organisation? They lose access immediately.',
  'members.noEmail': 'no email',
  'members.noEmail.source': 'no email',

  // -- Role picker -----------------------------------------
  'role.TENANT_ADMIN': 'Administers the organisation',
  'role.TENANT_ADMIN.source': 'Administers the organisation',
  'role.CAMPAIGN_ANALYST': 'Works on the campaigns they have access to',
  'role.CAMPAIGN_ANALYST.source': 'Works on the campaigns they have access to',
  'role.ADDRESS_READER': 'Sees the addresses on file',
  'role.ADDRESS_READER.source': 'Sees the addresses on file',
  'role.short.TENANT_ADMIN': 'Org admin',
  'role.short.TENANT_ADMIN.source': 'Org admin',
  'role.short.CAMPAIGN_ANALYST': 'Analyst',
  'role.short.CAMPAIGN_ANALYST.source': 'Analyst',
  'role.short.ADDRESS_READER': 'Address reader',
  'role.short.ADDRESS_READER.source': 'Address reader',
  'role.address.not_invitable':
    'This access does not go by invitation. You grant it afterwards, here in the people list.',
  'role.address.not_invitable.source':
    'This access does not go by invitation. You grant it afterwards, here in the people list.',
  'members.grant_address.confirm':
    'Grant {email} access to the addresses? They will see the addresses on file, and nothing ' +
    'else.',
  'members.grant_address.confirm.source':
    'Grant {email} access to the addresses? They will see the addresses on file, and nothing ' +
    'else.',
  'members.last_admin':
    'This person is the last administrator. Promote somebody else before changing this access.',
  'members.last_admin.source':
    'This person is the last administrator. Promote somebody else before changing this access.',

  // -- Invitations (§18.2) ---------------------------------------------------
  'invitations.heading': 'Invitations sent',
  'invitations.heading.source': 'Invitations sent',
  'invitations.create.emailLabel': "Invited person's email",
  'invitations.create.emailLabel.source': "Invited person's email",
  'invitations.create.roleLabel': 'Access',
  'invitations.create.roleLabel.source': 'Access',
  'invitations.create.roleHint': 'It takes effect the moment they accept.',
  'invitations.create.roleHint.source': 'It takes effect the moment they accept.',
  'invitations.revoke.confirm': 'Cancel this invitation? The link stops working immediately.',
  'invitations.revoke.confirm.source':
    'Cancel this invitation? The link stops working immediately.',

  // -- Credit requests (§18.6) ---------------------------------------------------
  'creditRequests.create.heading': 'Ask for more credits',
  'creditRequests.create.heading.source': 'Ask for more credits',

  // -- Balance, the acquisition status line ---------------
  'balance.zero.title': 'No account is being followed.',
  'balance.zero.title.source': 'No account is being followed.',
  'balance.zero.home': 'Your campaigns are ready. Credit is what is missing to start.',
  'balance.zero.home.source': 'Your campaigns are ready. Credit is what is missing to start.',
  'balance.zero.editor': 'Go ahead and save. Following starts once credits arrive.',
  'balance.zero.editor.source': 'Go ahead and save. Following starts once credits arrive.',
  'balance.zero.accounts': 'None of these accounts is being followed.',
  'balance.zero.accounts.source': 'None of these accounts is being followed.',
  'balance.zero.action': 'Request credits',
  'balance.zero.action.source': 'Request credits',
  'balance.negative.title': 'The balance has gone negative. Following has stopped.',
  'balance.negative.title.source': 'The balance has gone negative. Following has stopped.',
  'balance.unknown': 'We cannot show the balance right now.',
  'balance.unknown.source': 'We cannot show the balance right now.',
  'balance.unknown.action': 'Reload the page. Your accounts are still being followed.',
  'balance.unknown.action.source': 'Reload the page. Your accounts are still being followed.',

  'nothing_fetched.account': 'Account added. Following starts once there are credits.',
  'nothing_fetched.account.source': 'Account added. Following starts once there are credits.',
  'nothing_fetched.campaign': 'Campaign created. Now choose the accounts it will follow.',
  'nothing_fetched.campaign.source': 'Campaign created. Now choose the accounts it will follow.',

  'missed.title': 'What is on hold',
  'missed.title.source': 'What is on hold',
  'missed.planned': 'Collections planned',
  'missed.planned.source': 'Collections planned',
  'missed.unfunded': 'Collections missed',
  'missed.unfunded.source': 'Collections missed',
  'missed.since': 'On hold since',
  'missed.since.source': 'On hold since',
  'missed.permanent': 'What was posted in that time is not recovered later.',
  'missed.permanent.source': 'What was posted in that time is not recovered later.',
  'missed.toFund': 'Credits to cover it all',
  'missed.toFund.source': 'Credits to cover it all',
  'missed.toFund.until': 'Covers through {date}.',
  'missed.toFund.until.source': 'Covers through {date}.',
  'missed.toFund.unpriced': 'On request',
  'missed.toFund.unpriced.source': 'On request',

  'balance.partial.title': 'Not every account is being followed.',
  'balance.partial.title.source': 'Not every account is being followed.',
  'balance.partial.watched': 'Accounts watched',
  'balance.partial.watched.source': 'Accounts watched',
  'balance.partial.shed': 'Outside the window',
  'balance.partial.shed.source': 'Outside the window',
  'balance.partial.editor':
    "Go ahead and save. At today's balance the accounts at the end of the list stay out " +
    'until more credit arrives.',
  'balance.partial.editor.source':
    "Go ahead and save. At today's balance the accounts at the end of the list stay out " +
    'until more credit arrives.',
  'balance.partial.accounts': 'These accounts are out of the follow list right now.',
  'balance.partial.accounts.source': 'These accounts are out of the follow list right now.',
  'balance.partial.action': 'See which ones are out',
  'balance.partial.action.source': 'See which ones are out',
  'shed.title': 'Accounts left out',
  'shed.title.source': 'Accounts left out',
  'shed.why': "At today's balance not all of them can be followed at once.",
  'shed.why.source': "At today's balance not all of them can be followed at once.",
  'shed.lever.fewer': 'Follow fewer accounts',
  'shed.lever.fewer.source': 'Follow fewer accounts',
  'shed.lever.credits': 'Request more credits',
  'shed.lever.credits.source': 'Request more credits',

  // -- Onboarding ---------------------------------------
  'onboarding.title': 'Create your organisation',
  'onboarding.title.source': 'Create your organisation',
  'onboarding.stepLabel': 'Step 1 of 2',
  'onboarding.stepLabel.source': 'Step 1 of 2',
  'onboarding.name.label': 'Organisation name',
  'onboarding.name.label.source': 'Organisation name',
  'onboarding.name.hint': 'Visible to the whole team.',
  'onboarding.name.hint.source': 'Visible to the whole team.',
  'onboarding.submit': 'Continue',
  'onboarding.submit.source': 'Continue',
  'onboarding.failed': 'That did not work. Try again.',
  'onboarding.failed.source': 'That did not work. Try again.',
  'onboarding.unavailable':
    'You cannot create an organisation here yet. Ask somebody on your team who already uses ' +
    'the platform to invite this email.',
  'onboarding.unavailable.source':
    'You cannot create an organisation here yet. Ask somebody on your team who already uses ' +
    'the platform to invite this email.',

  // -- Onboarding: capacity (step 2 of 2, CA-6) ----------------------------
  'onboarding.capacity.stepLabel': 'Step 2 of 2',
  'onboarding.capacity.stepLabel.source': 'Step 2 of 2',
  'onboarding.capacity.title': 'How much will {organisation} need?',
  'onboarding.capacity.title.source': 'How much will {organisation} need?',
  'onboarding.capacity.lead':
    'The organisation is created now. Slots and credits are worked out with us and come in ' +
    'afterwards, without blocking sign-up.',
  'onboarding.capacity.lead.source':
    'The organisation is created now. Slots and credits are worked out with us and come in ' +
    'afterwards, without blocking sign-up.',
  'onboarding.capacity.slots.label': 'Campaign slots',
  'onboarding.capacity.slots.label.source': 'Campaign slots',
  'onboarding.capacity.slots.decrease': 'Decrease campaign slots',
  'onboarding.capacity.slots.decrease.source': 'Decrease campaign slots',
  'onboarding.capacity.slots.increase': 'Increase campaign slots',
  'onboarding.capacity.slots.increase.source': 'Increase campaign slots',
  'onboarding.capacity.credits.label': 'Credits',
  'onboarding.capacity.credits.label.source': 'Credits',
  'onboarding.capacity.credits.option': '{amount} thousand',
  'onboarding.capacity.credits.option.source': '{amount} thousand',
  'onboarding.capacity.credits.other': 'Other',
  'onboarding.capacity.credits.other.source': 'Other',
  'onboarding.capacity.context': 'Requested while creating the organisation.',
  'onboarding.capacity.context.source': 'Requested while creating the organisation.',
  'onboarding.capacity.failed': 'That request did not go through. Try again.',
  'onboarding.capacity.failed.source': 'That request did not go through. Try again.',
  'onboarding.capacity.skip': 'Create without asking now',
  'onboarding.capacity.skip.source': 'Create without asking now',
  'onboarding.capacity.submit': 'Create organisation',
  'onboarding.capacity.submit.source': 'Create organisation',

  // -- Invitation acceptance ----------------------------------------------
  'accept.page_title': 'Invitations',
  'accept.page_title.source': 'Invitations',
  'accept.invite.page_title': "You've been invited",
  'accept.invite.page_title.source': "You've been invited",
  'accept.page_subtitle': 'Here are the invitations for you to join organisations.',
  'accept.page_subtitle.source': 'Here are the invitations for you to join organisations.',
  'accept.switch_account_text': 'You are using the account {email}. Not you?',
  'accept.switch_account_text.source': 'You are using the account {email}. Not you?',
  'accept.switch_account_action': 'Sign out and continue with your own account',
  'accept.switch_account_action.source': 'Sign out and continue with your own account',
  'accept.verify_email.title': 'Confirm your email',
  'accept.verify_email.title.source': 'Confirm your email',
  'accept.verify_email.description':
    'To reach your invitations, we need to confirm this email is yours. Click the link we ' +
    'just sent you.',
  'accept.verify_email.description.source':
    'To reach your invitations, we need to confirm this email is yours. Click the link we ' +
    'just sent you.',
  'accept.verify_email.resend_text': "Didn't find the message?",
  'accept.verify_email.resend_text.source': "Didn't find the message?",
  'accept.verify_email.resend_action': 'Resend confirmation email',
  'accept.verify_email.resend_action.source': 'Resend confirmation email',
  'accept.invite.accept_action': 'Accept',
  'accept.invite.accept_action.source': 'Accept',
  'accept.invite.withdrawn_inline':
    'This invitation is no longer available. Ask whoever invited you for a new one.',
  'accept.invite.withdrawn_inline.source':
    'This invitation is no longer available. Ask whoever invited you for a new one.',
  'accept.empty_state':
    'There are no invitations waiting for this email. If you expected to find one here, ask ' +
    'your colleague to send it again.',
  'accept.empty_state.source':
    'There are no invitations waiting for this email. If you expected to find one here, ask ' +
    'your colleague to send it again.',
  'accept.refused.withdrawn': 'This invitation was cancelled. Ask your colleague for a new one.',
  'accept.refused.withdrawn.source':
    'This invitation was cancelled. Ask your colleague for a new one.',
  'accept.refused.expired':
    'This invitation is past its deadline. Ask whoever invited you to send a new one.',
  'accept.refused.expired.source':
    'This invitation is past its deadline. Ask whoever invited you to send a new one.',
  'accept.refused.already_accepted': 'You are already part of this organisation.',
  'accept.refused.already_accepted.source': 'You are already part of this organisation.',
  'accept.refused.already_accepted_action': 'Go to the organisation',
  'accept.refused.already_accepted_action.source': 'Go to the organisation',
  'accept.refused.invalid': 'This invitation cannot be used. Ask your team to send a new one.',
  'accept.refused.invalid.source':
    'This invitation cannot be used. Ask your team to send a new one.',
  'accept.refused.opaque':
    'This invitation could not be opened. Try asking your colleague for a new one.',
  'accept.refused.opaque.source':
    'This invitation could not be opened. Try asking your colleague for a new one.',

  // -- Request credits, the form -------------------------
  'request.title': 'Request credits',
  'request.title.source': 'Request credits',
  'request.lead': 'Tell us what you need to follow. The answer appears right here, on this page.',
  'request.lead.source':
    'Tell us what you need to follow. The answer appears right here, on this page.',
  'request.state.OPEN': 'Sent',
  'request.state.OPEN.source': 'Sent',
  'request.state.IN_REVIEW': 'Being looked at',
  'request.state.IN_REVIEW.source': 'Being looked at',
  'request.state.GRANTED': 'Granted',
  'request.state.GRANTED.source': 'Granted',
  'request.state.DECLINED': 'Declined',
  'request.state.DECLINED.source': 'Declined',
  'request.state.WITHDRAWN': 'Withdrawn',
  'request.state.WITHDRAWN.source': 'Withdrawn',
  'request.decided': 'Answered on',
  'request.decided.source': 'Answered on',
  'request.decision_note': "The team's reply",
  'request.decision_note.source': "The team's reply",
  'request.accounts.label': 'How many accounts do you plan to track?',
  'request.accounts.label.source': 'How many accounts do you plan to track?',
  'request.credits.label': 'How many credits do you want?',
  'request.credits.label.source': 'How many credits do you want?',
  'request.credits.hint': 'Not sure? Leave it blank and say below what you need.',
  'request.credits.hint.source': 'Not sure? Leave it blank and say below what you need.',
  'request.context.label': 'What you need to follow',
  'request.context.label.source': 'What you need to follow',
  'request.context.hint': 'Which accounts, over what period, and what must not be missed.',
  'request.context.hint.source': 'Which accounts, over what period, and what must not be missed.',
  'request.context.required': 'Write what you need to follow.',
  'request.context.required.source': 'Write what you need to follow.',
  'request.sent': 'Request sent on {date}. You can carry on setting up your campaigns meanwhile.',
  'request.sent.source':
    'Request sent on {date}. You can carry on setting up your campaigns meanwhile.',
  'request.open_exists':
    'You already have a request sent on {date}. To change anything, withdraw it and send ' +
    'another.',
  'request.open_exists.source':
    'You already have a request sent on {date}. To change anything, withdraw it and send ' +
    'another.',
  'request.submit': 'Send request',
  'request.submit.source': 'Send request',
  'request.withdraw': 'Withdraw request',
  'request.withdraw.source': 'Withdraw request',

  // -- Tenant home and campaign creation -------------------
  'home.heading': 'Dashboard',
  'home.heading.source': 'Dashboard',
  'home.newCampaign': 'New campaign',
  'home.newCampaign.source': 'New campaign',
  'campaigns.notCollected': 'Not being followed',
  'campaigns.notCollected.source': 'Not being followed',
  'newCampaign.submit': 'Create campaign',
  'newCampaign.submit.source': 'Create campaign',

  'invite.delivery.before':
    'You get a link to send to the person yourself. Nothing is sent automatically.',
  'invite.delivery.before.source':
    'You get a link to send to the person yourself. Nothing is sent automatically.',
  'invite.email.hint': 'Only somebody signing in with this address can accept.',
  'invite.email.hint.source': 'Only somebody signing in with this address can accept.',
  'invite.duplicate':
    'There is already an open invitation for this address. Cancel it before creating another.',
  'invite.duplicate.source':
    'There is already an open invitation for this address. Cancel it before creating another.',
  'invite.issued.title': 'Invitation created. Copy the link now.',
  'invite.issued.title.source': 'Invitation created. Copy the link now.',
  'invite.issued.once':
    'This link appears only once. If you leave without copying it, cancel the invitation ' +
    'and create another.',
  'invite.issued.once.source':
    'This link appears only once. If you leave without copying it, cancel the invitation ' +
    'and create another.',
  'invite.issued.send':
    'Now send the link to {email} however the two of you already talk. Nobody has been ' +
    'notified yet.',
  'invite.issued.send.source':
    'Now send the link to {email} however the two of you already talk. Nobody has been ' +
    'notified yet.',
  'invite.copy': 'Copy link',
  'invite.copy.source': 'Copy link',
  'invite.another': 'Invite somebody else',
  'invite.another.source': 'Invite somebody else',
  'invite.trigger': 'Invite person',
  'invite.trigger.source': 'Invite person',
  'invite.submit': 'Create invitation',
  'invite.submit.source': 'Create invitation',
  'invite.revoke': 'Cancel',
  'invite.revoke.source': 'Cancel',

  // -- Navigation -----------------------------------------------------------
  'nav.home': 'Dashboard',
  'nav.home.source': 'Dashboard',
  'nav.team': 'Users',
  'nav.team.source': 'Users',
};
